/**
 * Dedicated Pricing page (Zone 1) — `/pricing`.
 *
 * The four-tier comparison (with the monthly / annual toggle) plus the
 * plan-decision context the landing page's red-zone section keeps short: what
 * counts as a program, how the trial converts, and what happens when it
 * lapses. Drawn as the red zone of the same field as the landing page.
 * API-free like the rest of the marketing surface.
 */
import { Link } from 'react-router-dom';

import { ROUTES, TRIAL } from '../../lib/constants';
import { contactSalesEmail, HERO } from '../../lib/marketing-content';

import { YardLine } from './field';
import { MarketingChrome, TierGrid } from './marketing-chrome';

const PRICING_FAQ: ReadonlyArray<{ q: string; a: string }> = [
  {
    q: 'What counts as one subscription?',
    a: 'One football program in one team portal. Coaches, coordinators, stat operators and viewers all share it up to the plan’s seat limit; you never pay per coach.',
  },
  {
    q: `How does the ${TRIAL.days}-day trial work?`,
    a: `Every new team starts on the full ${TRIAL.planDisplayName} plan for ${TRIAL.days} days, no card required. Choose a plan any time during the trial. If you don’t, the team moves to ${TRIAL.downgradePlanDisplayName} on day ${TRIAL.days}.`,
  },
  {
    q: 'Can we change plans mid-season?',
    a: 'Yes. Upgrades take effect immediately with a prorated charge. Downgrades and cancellations take effect at the end of the current billing period, so you keep what you paid for.',
  },
  {
    q: 'Who owns the data?',
    a: 'Your program does. The platform stores football program data only, never student education records, and every team’s data is kept separate from every other team’s.',
  },
];

export function PricingPage(): JSX.Element {
  const salesEmail = contactSalesEmail();
  return (
    <MarketingChrome>
      <div className="pricing-field">
        <div className="pricing-field__turf" aria-hidden="true" />
        <section className="pricing-hero" aria-labelledby="pricing-title">
          <YardLine yard={80} />
          <p className="down-tag">
            <span className="down-tag__down">1st &amp; 10</span>
            <span className="down-tag__spot">Red zone</span>
          </p>
          <h1 id="pricing-title">One subscription per program</h1>
          <p className="pricing-hero__sub">
            Priced per program, not per coach. Every plan includes Play Entry with several
            operators, Game Summary and Play Archive.
          </p>
          <TierGrid />
          <p className="pricing-hero__more">{HERO.trialCallout}</p>
        </section>

        <section className="pricing-faq" aria-label="Pricing questions">
          <YardLine yard={90} />
          <h2>Before you pick a plan</h2>
          <dl className="pricing-faq__list">
            {PRICING_FAQ.map((item) => (
              <div key={item.q}>
                <dt>
                  <h3>{item.q}</h3>
                </dt>
                <dd>{item.a}</dd>
              </div>
            ))}
          </dl>
          <div className="btn-row pricing-faq__ctas">
            <Link className="btn btn--primary btn--lg" to={ROUTES.signup}>
              Start Free Trial
            </Link>
            {salesEmail === null ? null : (
              <a className="btn btn--chalk btn--lg" href={`mailto:${salesEmail}`}>
                Talk to sales
              </a>
            )}
          </div>
        </section>
      </div>
    </MarketingChrome>
  );
}
